/**
 * Race finish-time utilities.
 *
 * Inputs are a runner's total time for a given distance — distinct from
 * per-km pace handled in ./pace.ts. API stores integer seconds; UI lets
 * users type either MM:SS (e.g. "22:30" → 1350) or H:MM:SS (e.g. "1:30:00"
 * → 5400) and we round-trip back to the shortest sensible form.
 */

import { formatPaceSec } from './pace';

const MM_SS = /^(\d{1,3}):(\d{2})$/;
const H_MM_SS = /^(\d{1,2}):(\d{2}):(\d{2})$/;

export function parseFinishTime(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const hms = trimmed.match(H_MM_SS);
  if (hms) {
    const h = parseInt(hms[1], 10);
    const m = parseInt(hms[2], 10);
    const s = parseInt(hms[3], 10);
    if (m >= 60 || s >= 60) return null;
    return h * 3600 + m * 60 + s;
  }

  const ms = trimmed.match(MM_SS);
  if (ms) {
    const m = parseInt(ms[1], 10);
    const s = parseInt(ms[2], 10);
    if (s >= 60) return null;
    return m * 60 + s;
  }

  return null;
}

export function formatFinishTime(totalSec: number): string {
  const total = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export function toPacePerKm(totalSec: number, distanceKm: number): string {
  if (!Number.isFinite(totalSec) || totalSec <= 0 || distanceKm <= 0) return '';
  return formatPaceSec(Math.round(totalSec / distanceKm));
}
